import React from 'react';
import { ApiAccountDefinition } from '@api';
import { ModalStepTree } from '@components/common/multiStepModal/types';

/**
 * The extension points a resource type may fill in the account-management framework.
 *
 * The framework is generic: it knows how to list, create, edit and delete accounts,
 * but not what any particular resource's accounts *mean*. Where a resource needs to
 * depart from the default — a different create flow, a canonical form set, a
 * connectivity gate — it supplies an adapter here instead of the framework
 * special-casing it. The dependency therefore points inward (resource -> framework),
 * and a resource module is reachable only from the host that mounts it, not from the
 * framework itself.
 *
 * Every slot is optional. An absent adapter is the default behaviour, which is what
 * source and destination accounts use.
 */

/** Status of a connectivity ("Test Connection") check. */
export type ConnectionTestStatus = 'idle' | 'testing' | 'passed' | 'failed';

/** The live state of a connectivity check plus the two commands that drive it. */
export interface ConnectionTestController {
  status: ConnectionTestStatus;
  error?: string;
  /** Validate the form, then run the check. */
  test: () => void | Promise<void>;
  /** Drop any verdict, so a stale pass can never gate a persist. */
  reset: () => void;
}

export interface ConnectionTestControlProps {
  status: ConnectionTestStatus;
  error?: string;
  onTest: () => void;
  variant?: 'next';
}

/**
 * A resource's connectivity gate. Its presence is the capability: the framework
 * gates Save on `status === 'passed'` exactly when a resource supplies this slot,
 * so a gate can never be switched on without an affordance to satisfy it.
 */
interface ConnectionTestSlot {
  /**
   * Hook driving the check for the account under test.
   *
   * Called unconditionally by the edit step, against a controller chosen once from
   * the store's adapter — see `useNoConnectionTest` for why that is safe.
   */
  useController: (args: { type?: string; accountId?: string }) => ConnectionTestController;
  /** Renders the trigger and its inline verdict. */
  Control: React.FC<ConnectionTestControlProps>;
}

export interface AccountResourceAdapter {
  /**
   * The create sub-tree, replacing the framework's account-type-picker -> form flow.
   * Its root key must match the key the account picker's "Add account" navigates to.
   */
  getCreateStepTree?: () => ModalStepTree;

  /**
   * Rewrite a backend account definition to this resource's canonical presentation,
   * so every downstream consumer (edit form, option summary, prefill) renders the
   * same field set regardless of drift in the backend `uiConfig`. Applied only to
   * definitions in `supportedAccountTypes`.
   */
  reconcileDefinition?: (definition: ApiAccountDefinition) => ApiAccountDefinition;

  /**
   * The `options` an account's definition implies but its edit form never renders.
   * A PATCH replaces the whole `options` blob rather than merging per key, so these
   * must ride along on an edit or they are erased from the stored account.
   */
  getDefinitionImpliedOptions?: (definitionName: string) => Record<string, unknown>;

  /** Connectivity validation required before a create or edit persists. */
  connectionTest?: ConnectionTestSlot;
}

const NO_CONNECTION_TEST: ConnectionTestController = {
  status: 'idle',
  error: undefined,
  test: () => undefined,
  reset: () => undefined,
};

/**
 * The controller used when a resource has no connectivity gate.
 *
 * It calls no hooks, which is safe — and the reason the edit step may pick its
 * controller from the adapter and still call it unconditionally. Hook order only has
 * to be stable across renders of one component instance, and the choice is made from
 * `store.adapter`: a readonly field, set once in the constructor, on a store that its
 * host memoises for the component's lifetime. So the branch is fixed before the first
 * render and cannot flip. Nothing reads the values it returns either: they are live
 * only where `requiresConnectionTest` is true, which is exactly where a real
 * controller has replaced this one.
 */
export const useNoConnectionTest = (): ConnectionTestController => NO_CONNECTION_TEST;
